import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HiDownload, HiEye } from "react-icons/hi";
import { HiCodeBracket } from "react-icons/hi2";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import * as types from "types";
import useDownloadFile from "services/cortevaChain/hooks/useDownloadFile";
import saveBlob from "utilities/saveBlob";
import {
  getPreviewKind,
  hasToggleView,
  TEXT_SIZE_LIMIT,
  type PreviewKind,
} from "./filePreviewUtils";
import styles from "./FilePreviewModal.module.scss";

interface FilePreviewModalProps {
  fileName: string;
  chatThread: types.ChatThread;
  permalink: string;
  /** When set, overrides getPreviewKind(fileName) — used for .pptx previewed as a pdf_preview variant. */
  previewKindOverride?: PreviewKind;
  /** When set, the Download button fetches this URL instead of permalink (e.g. original .pptx while previewing a pdf_preview variant). */
  downloadPermalink?: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function FilePreviewModal({
  fileName,
  chatThread,
  permalink,
  previewKindOverride,
  downloadPermalink,
  isOpen,
  onClose,
}: FilePreviewModalProps) {
  const derivedPreview = getPreviewKind(fileName);
  const preview = previewKindOverride
    ? {
        ...derivedPreview,
        kind: previewKindOverride,
        mimeType: "application/pdf",
      }
    : derivedPreview;
  const ext = fileName.toLowerCase().split(".").pop() ?? "";
  const { data } = useDownloadFile({ chatThread, fileName, permalink });
  const { data: downloadData } = useDownloadFile({
    chatThread,
    fileName,
    permalink: downloadPermalink ?? permalink,
  });
  // Pair decoded text with the blob it came from so that when data changes
  // (e.g. after an edit_file cache invalidation), the stale text is immediately
  // null via identity comparison — no synchronous setState in the effect needed.
  const [textDecode, setTextDecode] = useState<{
    blob: Blob;
    text: string | null;
    error: string | null;
  } | null>(null);
  const codeText =
    textDecode !== null && textDecode.blob === data ? textDecode.text : null;
  const codeTextError =
    textDecode !== null && textDecode.blob === data ? textDecode.error : null;
  const [showSource, setShowSource] = useState(false);

  // Reset toggle when modal opens — setState during render is the React-recommended
  // pattern for "adjusting state when a prop changes"
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setShowSource(false);
  }

  // Blob URL for html, pdf, image — same pattern as ImagePreview (no revoke).
  const blobUrl = useMemo(() => {
    if (!data || preview.kind === "code" || preview.kind === "unsupported")
      return null;
    const typed = new Blob([data], { type: preview.mimeType });
    return URL.createObjectURL(typed);
  }, [data, preview.kind, preview.mimeType]);

  // Size-based error derived from data — no effect needed
  const sizeError =
    data && preview.kind === "code" && data.size > TEXT_SIZE_LIMIT
      ? "File is too large to preview (> 1 MB). Download to view."
      : null;

  // Text for: code files, html source toggle, svg source toggle
  useEffect(() => {
    const needsText =
      preview.kind === "code" ||
      preview.kind === "html" ||
      (preview.kind === "image" && ext === "svg");
    if (!data || !needsText || sizeError) return;
    const blob = data;
    blob
      .text()
      .then((text) => setTextDecode({ blob, text, error: null }))
      .catch(() =>
        setTextDecode({
          blob,
          text: null,
          error: "Failed to read file contents.",
        })
      );
  }, [data, preview.kind, ext, sizeError]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleDownload = () => {
    const blob = downloadData ?? data;
    if (!blob) return;
    saveBlob(blob, fileName);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  const error = sizeError ?? codeTextError;

  const renderSource = (language: string) => (
    <div className={styles.codeContainer}>
      <SyntaxHighlighter
        language={language}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{ margin: 0, height: "100%", borderRadius: 0 }}
      >
        {codeText ?? ""}
      </SyntaxHighlighter>
    </div>
  );

  const renderBody = () => {
    if (error) return <div className={styles.errorMessage}>{error}</div>;

    // HTML — rendered view needs blobUrl; source view needs codeText
    if (preview.kind === "html") {
      if (showSource) {
        if (codeText === null)
          return <div className={styles.loading}>Loading…</div>;
        return renderSource("html");
      }
      if (blobUrl === null)
        return <div className={styles.loading}>Loading…</div>;
      return (
        <iframe
          className={styles.iframe}
          src={blobUrl}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          title={fileName}
        />
      );
    }

    // PDF
    if (preview.kind === "pdf") {
      if (blobUrl === null)
        return <div className={styles.loading}>Loading…</div>;
      return (
        <iframe className={styles.iframe} src={blobUrl} title={fileName} />
      );
    }

    // Image — rendered view needs blobUrl; SVG source view needs codeText
    if (preview.kind === "image") {
      if (ext === "svg" && showSource) {
        if (codeText === null)
          return <div className={styles.loading}>Loading…</div>;
        return renderSource("xml");
      }
      if (blobUrl === null)
        return <div className={styles.loading}>Loading…</div>;
      return (
        <div className={styles.imageContainer}>
          <img className={styles.image} src={blobUrl} alt={fileName} />
        </div>
      );
    }

    // Code / text — all paths need codeText
    if (codeText === null)
      return <div className={styles.loading}>Loading…</div>;

    if (ext === "md" && !showSource) {
      return (
        <div className={styles.markdownBody}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{codeText}</ReactMarkdown>
        </div>
      );
    }
    if (ext === "json" && !showSource) {
      let pretty = codeText;
      try {
        pretty = JSON.stringify(JSON.parse(codeText), null, 2);
      } catch {
        // malformed JSON — fall through to raw text
      }
      return (
        <div className={styles.codeContainer}>
          <SyntaxHighlighter
            language="json"
            style={oneDark}
            showLineNumbers
            wrapLongLines
            customStyle={{ margin: 0, height: "100%", borderRadius: 0 }}
          >
            {pretty}
          </SyntaxHighlighter>
        </div>
      );
    }
    return renderSource(preview.language ?? "text");
  };

  const canToggle = hasToggleView(preview.kind, ext);

  const modalContent = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.fileName} title={fileName}>
            {fileName}
          </span>
          <div className={styles.headerActions}>
            {canToggle && (
              <button
                className={styles.toggleButton}
                onClick={() => setShowSource((v) => !v)}
                title={showSource ? "Show rendered" : "Show source"}
              >
                {showSource ? <HiEye size={14} /> : <HiCodeBracket size={14} />}
                {showSource ? "Rendered" : "Source"}
              </button>
            )}
            <button
              className={styles.iconButton}
              onClick={handleDownload}
              disabled={downloadPermalink ? !downloadData : !data}
              title="Download file"
            >
              <HiDownload size={18} />
            </button>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close preview"
            >
              ✕
            </button>
          </div>
        </div>
        <div className={styles.body}>{renderBody()}</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
